import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Divider,
  Form,
  message,
  Radio,
  Row,
  Space,
  Switch,
  Select,
  Typography
} from 'antd';
import {
  CloudUploadOutlined,
  ReloadOutlined,
  SafetyOutlined,
  SyncOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useI18n } from '../hooks/useI18n';
import {
  fetchPermissionSwitches,
  syncPermissionSwitches,
  updatePermissionSwitches,
  publishPermissionSwitches,
  PermissionSwitchConfigRecord
} from '../services/chatBIPermissionService';
import { companyService, Company, buildProjectIdSelectOptions } from '../services/companyService';

const { Title, Text } = Typography;

interface FormValues {
  activeSystem: string;
  switches: Record<string, any>;
}

interface SwitchState {
  AgentPermission: {
    open?: boolean;
    enabled?: boolean;
  };
  B1RolePermission: {
    open?: boolean;
    enabled?: boolean;
    type: {
      'model-branch'?: boolean;
      'date-ownership'?: boolean;
    };
  };
}

const DEFAULT_SWITCHES: SwitchState = {
  AgentPermission: {
    open: false,
    enabled: false
  },
  B1RolePermission: {
    open: false,
    enabled: false,
    type: {
      'model-branch': false,
      'date-ownership': false
    }
  }
};

const mergeSwitches = (source?: Record<string, any> | null): SwitchState => {
  const base: SwitchState = {
    AgentPermission: {
      ...DEFAULT_SWITCHES.AgentPermission
    },
    B1RolePermission: {
      ...DEFAULT_SWITCHES.B1RolePermission,
      type: {
        ...DEFAULT_SWITCHES.B1RolePermission.type
      }
    }
  };

  if (!source) {
    return base;
  }

  if (source.AgentPermission) {
    base.AgentPermission = {
      ...base.AgentPermission,
      ...source.AgentPermission
    };
  }

  if (source.B1RolePermission) {
    base.B1RolePermission = {
      ...base.B1RolePermission,
      ...source.B1RolePermission,
      type: {
        ...base.B1RolePermission.type,
        ...(source.B1RolePermission.type || {})
      }
    };
  }

  return base;
};

const PermissionSwitchConfig: React.FC = () => {
  const { t } = useI18n();
  const [form] = Form.useForm<FormValues>();
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [latestRecord, setLatestRecord] = useState<PermissionSwitchConfigRecord[] | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [projectId, setProjectId] = useState<string>('all');

  const projectSelectOptions = useMemo(
    () => buildProjectIdSelectOptions(companies, t('common.all')),
    [companies, t]
  );

  useEffect(() => {
    if (projectId === 'all') return;
    const ok = projectSelectOptions.some((o) => o.value === projectId);
    if (!ok) setProjectId('all');
  }, [projectId, projectSelectOptions]);

  const activeSystem = Form.useWatch('activeSystem', form);
  const isAgentActive = activeSystem === 'AgentPermission';
  const isB1Active = activeSystem === 'B1RolePermission';

  const setActiveSystem = (system: string) => {
    form.setFieldsValue({ activeSystem: system });
  };

  useEffect(() => {
    if (!activeSystem) {
      return;
    }

    const current: Record<string, any> = form.getFieldValue('switches') || {};
    const merged = mergeSwitches(current);

    if (activeSystem === 'AgentPermission') {
      merged.AgentPermission.enabled = true;
      merged.AgentPermission.open = true;
      merged.B1RolePermission.enabled = false;
      merged.B1RolePermission.open = false;
      merged.B1RolePermission.type = {
        ...merged.B1RolePermission.type,
        'model-branch': false,
        'date-ownership': false
      };
    } else if (activeSystem === 'B1RolePermission') {
      merged.AgentPermission.enabled = false;
      merged.AgentPermission.open = false;
      merged.B1RolePermission.enabled = true;
      merged.B1RolePermission.open = true;
      merged.B1RolePermission.type = {
        ...merged.B1RolePermission.type,
        'model-branch': merged.B1RolePermission.type['model-branch'] ?? false,
        'date-ownership': merged.B1RolePermission.type['date-ownership'] ?? false
      };
    }

    form.setFieldsValue({ switches: merged });
  }, [activeSystem, form]);

  const applyFormValues = (records: PermissionSwitchConfigRecord[] | null, switches: Record<string, any> | null) => {
    const merged = mergeSwitches(switches);
    let activeSystem = 'AgentPermission';

    if (records && records.length) {
      records.forEach(record => {
        if (record.type === 'AgentPermission') {
          merged.AgentPermission.enabled = record.enabled ?? merged.AgentPermission.enabled;
        } else if (record.type === 'B1RolePermission') {
          merged.B1RolePermission.enabled = record.enabled ?? merged.B1RolePermission.enabled;
          merged.B1RolePermission.type = {
            'model-branch': record.model_branch ?? merged.B1RolePermission.type['model-branch'],
            'date-ownership': record.data_ownership ?? merged.B1RolePermission.type['date-ownership']
          };
        }
      });

      const activeRecord = records.find(r => r.enabled);
      if (activeRecord) {
        activeSystem = activeRecord.type;
      }
    } else if (switches) {
      merged.AgentPermission.enabled = typeof switches?.AgentPermission?.enabled === 'boolean'
        ? switches.AgentPermission.enabled
        : merged.AgentPermission.enabled;
      merged.B1RolePermission.enabled = typeof switches?.B1RolePermission?.enabled === 'boolean'
        ? switches.B1RolePermission.enabled
        : merged.B1RolePermission.enabled;
      merged.B1RolePermission.type = {
        'model-branch': switches?.B1RolePermission?.type?.['model-branch'] ?? merged.B1RolePermission.type['model-branch'],
        'date-ownership': switches?.B1RolePermission?.type?.['date-ownership'] ?? merged.B1RolePermission.type['date-ownership']
      };

      if (merged.B1RolePermission.enabled) {
        activeSystem = 'B1RolePermission';
      } else if (merged.AgentPermission.enabled) {
        activeSystem = 'AgentPermission';
      }
    }

    form.setFieldsValue({
      activeSystem,
      switches: merged
    });
  };

  const loadLatest = async (options?: { source?: 'local' | 'remote' }) => {
    setLoading(true);
    try {
      const response = await fetchPermissionSwitches({
        ...options,
        projectId: projectId === 'all' ? null : projectId,
      });
      if (response.success) {
        const records = Array.isArray(response.data.record) ? response.data.record : response.data.record ? [response.data.record] : [];
        const switches = response.data.switches || null;
        setLatestRecord(records);
        applyFormValues(records.length ? records : null, switches);
      } else {
        message.warning(response.message || t('chatbi.permissionSwitch.message.loadFailed'));
      }
    } catch (error: any) {
      message.error(
        error?.response?.data?.message ||
          error?.message ||
          t('chatbi.permissionSwitch.message.loadFailed')
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLatest();
  }, []);

  useEffect(() => {
    const loadCompanies = async () => {
      try {
        const currentTenant = localStorage.getItem('currentTenant') || 'default';
        const res = await companyService.getCompanies(currentTenant);
        if (res?.success) {
          setCompanies(res.data || []);
          if (projectId === 'all' && (res.data || []).length > 0) {
            const firstPid = (res.data as Company[]).find(
              (c) => c.project_id != null && String(c.project_id).trim() !== ''
            )?.project_id;
            if (firstPid != null) setProjectId(String(firstPid));
          }
        }
      } catch {
        // ignore
      }
    };
    loadCompanies();
  }, []);

  useEffect(() => {
    loadLatest({ source: 'local' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const handleSyncRemote = async () => {
    setSyncing(true);
    try {
      const response = await syncPermissionSwitches(projectId === 'all' ? null : projectId);
      if (response.success) {
        message.success(t('chatbi.permissionSwitch.message.syncSuccess'));
        const records = Array.isArray(response.data.record) ? response.data.record : response.data.record ? [response.data.record] : [];
        const switches = response.data.switches || null;
        setLatestRecord(records);
        applyFormValues(records.length ? records : null, switches);
      } else {
        message.warning(response.message || t('chatbi.permissionSwitch.message.syncFailed'));
      }
    } catch (error: any) {
      message.error(
        error?.response?.data?.message ||
          error?.message ||
          t('chatbi.permissionSwitch.message.syncFailed')
      );
    } finally {
      setSyncing(false);
    }
  };

  const handleReloadLocal = async () => {
    loadLatest({ source: 'local' });
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      const response = await publishPermissionSwitches(projectId === 'all' ? null : projectId);
      if (response.success) {
        message.success(t('chatbi.permissionSwitch.message.publishSuccess'));
        const records = Array.isArray(response.data.record) ? response.data.record : response.data.record ? [response.data.record] : [];
        const switches = response.data.switches || null;
        setLatestRecord(records);
        applyFormValues(records.length ? records : null, switches);
      } else {
        message.warning(response.message || t('chatbi.permissionSwitch.message.publishFailed'));
      }
    } catch (error: any) {
      message.error(
        error?.response?.data?.message ||
          error?.message ||
          t('chatbi.permissionSwitch.message.publishFailed')
      );
    } finally {
      setPublishing(false);
    }
  };

  const validateBeforeSubmit = (values: FormValues) => {
    if (values.activeSystem === 'B1RolePermission') {
      const modelBranch = values.switches?.B1RolePermission?.type?.['model-branch'];
      const dataOwnership = values.switches?.B1RolePermission?.type?.['date-ownership'];
      if (typeof modelBranch !== 'boolean') {
        throw new Error(t('chatbi.permissionSwitch.validation.requireModelBranch'));
      }
      if (typeof dataOwnership !== 'boolean') {
        throw new Error(t('chatbi.permissionSwitch.validation.requireDataOwnership'));
      }
    }
  };

  const handleSubmit = async (rawValues: FormValues) => {
    const merged = mergeSwitches(rawValues.switches);
    const normalizedSwitches: SwitchState = {
      AgentPermission: {
        enabled: merged.AgentPermission.enabled ?? merged.AgentPermission.open ?? false
      },
      B1RolePermission: {
        enabled: merged.B1RolePermission.enabled ?? merged.B1RolePermission.open ?? false,
        type: {
          ...merged.B1RolePermission.type
        }
      }
    };

    if (rawValues.activeSystem === 'AgentPermission') {
      normalizedSwitches.AgentPermission.enabled = true;
      normalizedSwitches.B1RolePermission.enabled = false;
      normalizedSwitches.B1RolePermission.type = {
        'model-branch': false,
        'date-ownership': false
      };
    } else {
      normalizedSwitches.AgentPermission.enabled = false;
      normalizedSwitches.B1RolePermission.enabled = true;
      normalizedSwitches.B1RolePermission.type = {
        'model-branch': typeof normalizedSwitches.B1RolePermission.type['model-branch'] === 'boolean'
          ? normalizedSwitches.B1RolePermission.type['model-branch']
          : false,
        'date-ownership': typeof normalizedSwitches.B1RolePermission.type['date-ownership'] === 'boolean'
          ? normalizedSwitches.B1RolePermission.type['date-ownership']
          : false
      };
    }

    const values: FormValues = {
      ...rawValues,
      switches: normalizedSwitches
    };

    try {
      validateBeforeSubmit(values);
    } catch (validationError: any) {
      message.error(validationError.message);
      return;
    }

    setSaving(true);
    try {
      const response = await updatePermissionSwitches(
        {
          activeSystem: values.activeSystem,
          switches: values.switches
        },
        projectId === 'all' ? null : projectId
      );

      if (response.success) {
        message.success(t('chatbi.permissionSwitch.message.updateSuccess'));
        const records = Array.isArray(response.data.record) ? response.data.record : response.data.record ? [response.data.record] : [];
        const switches = response.data.switches || null;
        setLatestRecord(records);
        applyFormValues(records.length ? records : null, switches);
      } else {
        message.warning(response.message || t('chatbi.permissionSwitch.message.updateFailed'));
      }
    } catch (error: any) {
      message.error(
        error?.response?.data?.message ||
          error?.message ||
          t('chatbi.permissionSwitch.message.updateFailed')
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <Row align="middle" justify="start" style={{ flexWrap: 'wrap' }}>
          <Space align="baseline" size={16} style={{ flexWrap: 'wrap' }}>
            <Title level={2} style={{ margin: 0 }}>
              <SafetyOutlined style={{ marginRight: '8px' }} />
              {t('chatbi.permissionSwitch.title')}
            </Title>
            <Select
              showSearch
              optionFilterProp="label"
              style={{ width: 280 }}
              value={projectId}
              onChange={setProjectId}
              options={projectSelectOptions}
            />
            <Text type="secondary">{t('chatbi.permissionSwitch.description')}</Text>
            <Space style={{ flexWrap: 'wrap' }}>
              <Button icon={<ReloadOutlined />} onClick={handleReloadLocal}>
                {t('chatbi.permissionSwitch.actions.reload')}
              </Button>
              <Button
                icon={<CloudUploadOutlined />}
                loading={publishing}
                onClick={handlePublish}
              >
                {t('chatbi.permissionSwitch.actions.publish')}
              </Button>
              <Button
                type="primary"
                icon={<SyncOutlined />}
                loading={syncing}
                onClick={handleSyncRemote}
              >
                {t('chatbi.permissionSwitch.actions.sync')}
              </Button>
            </Space>
          </Space>
        </Row>
      </div>

      <Card loading={loading} style={{ marginBottom: '24px' }}>
        {latestRecord && latestRecord.length > 0 && (
          <Alert
            style={{ marginBottom: 16 }}
            type="info"
            message={t('chatbi.permissionSwitch.status.latest', {
              type: latestRecord.find(record => record.enabled)?.type || latestRecord[0].type,
              time: latestRecord[0].synced_at
                ? dayjs(latestRecord[0].synced_at).format('YYYY-MM-DD HH:mm:ss')
                : t('chatbi.permissionSwitch.message.noSyncTime')
            })}
          />
        )}

        <Form<FormValues> form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="activeSystem"
            label={t('chatbi.permissionSwitch.fields.activeSystem')}
            rules={[{ required: true, message: t('chatbi.permissionSwitch.validation.chooseSystem') }]}
          >
            <Radio.Group>
              <Radio value="AgentPermission">AgentPermission</Radio>
              <Radio value="B1RolePermission">B1RolePermission</Radio>
            </Radio.Group>
          </Form.Item>

          <Divider />

          <Title level={5} style={{ marginTop: 0 }}>
            {t('chatbi.permissionSwitch.sections.agentPermissions')}
          </Title>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Form.Item
              label={t('chatbi.permissionSwitch.fields.agentOpen')}
              colon={false}
            >
              <Switch
                checked={isAgentActive}
                onChange={(checked) => {
                  if (checked) {
                    setActiveSystem('AgentPermission');
                  } else {
                    setActiveSystem('B1RolePermission');
                  }
                }}
              />
            </Form.Item>
          </Space>

          <Divider />

          <Title level={5} style={{ marginTop: 0 }}>
            {t('chatbi.permissionSwitch.sections.b1Permissions')}
          </Title>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Form.Item
              label={t('chatbi.permissionSwitch.fields.b1Open')}
              colon={false}
            >
              <Switch
                checked={isB1Active}
                onChange={(checked) => {
                  if (checked) {
                    setActiveSystem('B1RolePermission');
                  } else {
                    setActiveSystem('AgentPermission');
                  }
                }}
              />
            </Form.Item>
            {isB1Active && (
              <>
                <Form.Item
                  label={t('chatbi.permissionSwitch.fields.modelBranch')}
                  name={['switches', 'B1RolePermission', 'type', 'model-branch']}
                  valuePropName="checked"
                  rules={[
                    {
                      validator: (_, value) => {
                        if (!isB1Active) {
                          return Promise.resolve();
                        }
                        if (typeof value !== 'boolean') {
                          return Promise.reject(new Error(t('chatbi.permissionSwitch.validation.requireModelBranch')));
                        }
                        return Promise.resolve();
                      }
                    }
                  ]}
                >
                  <Switch />
                </Form.Item>
                <Form.Item
                  label={t('chatbi.permissionSwitch.fields.dateOwnership')}
                  name={['switches', 'B1RolePermission', 'type', 'date-ownership']}
                  valuePropName="checked"
                  rules={[
                    {
                      validator: (_, value) => {
                        if (!isB1Active) {
                          return Promise.resolve();
                        }
                        if (typeof value !== 'boolean') {
                          return Promise.reject(new Error(t('chatbi.permissionSwitch.validation.requireDataOwnership')));
                        }
                        return Promise.resolve();
                      }
                    }
                  ]}
                >
                  <Switch />
                </Form.Item>
              </>
            )}
          </Space>

          <Divider />

          <Space>
            <Button type="primary" htmlType="submit" loading={saving}>
              {t('chatbi.permissionSwitch.actions.save')}
            </Button>
          </Space>
        </Form>
      </Card>
    </div>
  );
};

export default PermissionSwitchConfig;

